import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AgEditRemoveAccessButtonComponent } from '@app/v2/access-management/ag-remove-access-button/ag-remove-access-button.component';
import { AgRemoveButtonComponent } from '@app/v2/access-management/ag-remove-button/ag-remove-button.component';
import { AgSwitchButtonComponent } from '@app/v2/access-management/ag-switch-button/ag-switch-button.component';
import { AgRoleBadgeRendererComponent } from '@app/v2/access-management/manage-user-access/ag-role-badge-renderer/ag-role-badge-renderer.component';
import { AgUserInfoRendererComponent } from '@app/v2/access-management/manage-user-access/ag-user-info-renderer/ag-user-info-renderer.component';
import { AgAllocateBtnComponent } from '@app/v2/entitlement/ag-allocate-btn/ag-allocate-btn.component';
import { AgBundleDetailsComponent } from '@app/v2/entitlement/ag-bundle-details/ag-bundle-details.component';
import { AgDeallocateBtnComponent } from '@app/v2/entitlement/ag-deallocate-btn/ag-deallocate-btn.component';
import { AgServiceDetailsComponent } from '@app/v2/entitlement/ag-service-details/ag-service-details.component';
import { AgStatusRendererComponent } from '@app/v2/entitlement/ag-status-renderer/ag-status-renderer.component';
import { AllocateQuantityComponent } from '@app/v2/entitlement/allocate-entitlement/allocate-quantity/allocate-quantity.component';
import { AgInvitationActionButtonComponent } from '@app/v2/invitation-management/ag-invitation-action-button/ag-invitation-action-button.component';
import { AgInvitationStatusRendererComponent } from '@app/v2/invitation-management/ag-invitation-status-renderer/ag-invitation-status-renderer.component';
import { AgOrganizationActionsMenuRendererComponent } from '@app/v2/standalone/organizations/ag-renderer/ag-organization-actions-menu-renderer/ag-organization-actions-menu-renderer.component';
import { OrganizationColumnRendererComponent } from '@app/v2/standalone/organizations/ag-renderer/ag-organization-column-renderer/organization-column-renderer.component';
import { AgOrganizationDetailsRendererComponent } from '@app/v2/standalone/organizations/ag-renderer/ag-organization-details-renderer/ag-organization-details-renderer.component';
import { AgTokensActivityComponent } from '@app/v2/utility-tokens/ledger/ag-credits-activity/ag-credits-activity.component';
import { GridRowModelTypes, TextButtonComponent } from '@ra-web-tech-ui-toolkit/components';
import { AgGridAngular } from 'ag-grid-angular';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ColDef,
  FilterChangedEvent,
  FilterModel,
  FirstDataRenderedEvent,
  GetRowIdFunc,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  Module,
  PaginationChangedEvent,
  ProcessCellForExportParams,
  RowClickedEvent,
  RowDataUpdatedEvent,
  RowSelectedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ClipboardModule,
  ExcelExportModule,
  ServerSideRowModelModule,
  SetFilterModule,
  TreeDataModule,
} from 'ag-grid-enterprise';
import { filter, Observable, Subscription, tap } from 'rxjs';
import { AgCustomNoRowsOverlayComponent } from '../ag-renderers/ag-custom-no-rows-overlay/ag-custom-no-rows-overlay.component';
import { AgTimeFormatComponent } from '../ag-renderers/ag-time-format.component';
import { AgTwoButtonRenderComponent } from '../ag-renderers/ag-two-buttons-render/ag-two-buttons-render.component';
import { CustomDatePipe } from '../pipes/date/custom-date.pipe';
import { formatEntitlementCopyColumnContent } from '../utils';

@Component({
  selector: 'app-ag-toolbar-grid',
  templateUrl: './ag-toolbar-grid.component.html',
  styleUrl: './ag-toolbar-grid.component.scss',
  imports: [TextButtonComponent, AgGridAngular],
})
export class AgToolbarGridComponent implements OnInit, OnDestroy {
  @Input() noRowsOverlayMsg: string = 'No matching rows found';
  @Input() noRowsOverlayContent: string = '';
  @Input() columnDefs!: ColDef[];
  @Input() pageSize: number = 10;
  @Input() gridOptions!: GridOptions;
  @Input() dataProvider?: () => IServerSideDatasource;
  @Input() rowData$?: Observable<any>;
  @Input() showToolbar: boolean = true;
  @Output() gridReady = new EventEmitter<any>();
  @Output() firstDataRendered = new EventEmitter<any>();
  @Output() rowDataUpdated = new EventEmitter<any>();
  @Output() rowSelected = new EventEmitter<RowSelectedEvent>();
  @Output() rowClicked = new EventEmitter<RowClickedEvent>();
  @Output() sortChanged = new EventEmitter<SortChangedEvent>();
  @Output() filterChanged = new EventEmitter<FilterChangedEvent>();
  @Output() paginationChanged = new EventEmitter<PaginationChangedEvent>();
  @Output() clearAllSortFilters = new EventEmitter<void>();
  @Input() getRowId?: GetRowIdFunc<any>;
  @Input() defaultFilter?: FilterModel;
  @Input() refreshOn$?: Observable<boolean | undefined>;
  @Input() hideClearFilter?: boolean = false;

  DEFAULT_GRID_OPTIONS: GridOptions = {
    animateRows: true,
    domLayout: 'autoHeight',
    cellSelection: true,
    pagination: true,
    rowSelection: { mode: 'singleRow', checkboxes: false, enableClickSelection: false },
    icons: { menu: '<i class="ra-icon-ide-sm-filter custom-filter-icon"></i>' },
  };

  gridApi: any;
  private subscription: Subscription;
  disableClearFilter: boolean = false;

  ngOnInit(): void {
    const defaultColDef = {
      minWidth: 100,
      autoHeight: true,
      filter: false,
      flex: 1,
      resizable: true,
      unSortIcon: true,
    };
    const getRowHeight = (): number => 60;
    const loadingTemplate = '<span class="ag-overlay-loading-center">Data is loading...</span>';
    const noRowsOverlayComponentParams: any = {
      title: this.noRowsOverlayMsg,
      content: this.noRowsOverlayContent,
    };
    // fix names for consistency
    const components = {
      serviceDetailsRenderer: AgServiceDetailsComponent,
      statusRenderer: AgStatusRendererComponent,
      timeFormatRenderer: AgTimeFormatComponent,
      twoButtonRender: AgTwoButtonRenderComponent,
      editRemoveAccessButtonRenderer: AgEditRemoveAccessButtonComponent,
      bundleDetailsRenderer: AgBundleDetailsComponent,
      allocateButtonRenderer: AgAllocateBtnComponent,
      switchButtonRenderer: AgSwitchButtonComponent,
      deallocateButtonRenderer: AgDeallocateBtnComponent,
      removeButtonRenderer: AgRemoveButtonComponent,
      organizationColumnRenderer: OrganizationColumnRendererComponent,
      agInvitationActionButtonComponent: AgInvitationActionButtonComponent,
      agInvitationStatusRendererComponent: AgInvitationStatusRendererComponent,
      creditsActivity: AgTokensActivityComponent,
      allocateQuantity: AllocateQuantityComponent,
      organizationDetailsRenderer: AgOrganizationDetailsRendererComponent,
      organizationActionsMenuRenderer: AgOrganizationActionsMenuRendererComponent,
      userInfoRenderer: AgUserInfoRendererComponent,
      roleBadgeRenderer: AgRoleBadgeRendererComponent,
    };
    const otherInitialOptions: GridOptions = {
      columnDefs: this.columnDefs,
      components: components,
      defaultColDef: defaultColDef,
      getRowHeight: getRowHeight,
      noRowsOverlayComponent: AgCustomNoRowsOverlayComponent,
      noRowsOverlayComponentParams: noRowsOverlayComponentParams,
      overlayLoadingTemplate: loadingTemplate,
      paginationPageSize: this.pageSize,
      paginationPageSizeSelector: false,
      processCellForClipboard: this.processCellForClipboard.bind(this),
    };

    this.gridOptions = Object.assign({}, this.DEFAULT_GRID_OPTIONS, otherInitialOptions, this.gridOptions);

    if (this.dataProvider) {
      this.gridOptions.rowModelType = 'serverSide';
      this.gridOptions.cacheBlockSize = this.pageSize;
      this.getRowId ??= (params): string => params.data.id;
    } else {
      this.gridOptions.rowModelType = GridRowModelTypes.ClientSide;
    }

    this.subscription =
      this.refreshOn$
        ?.pipe(
          filter((refresh) => refresh !== undefined && refresh),
          tap(() => {
            if (!this.gridApi || this.gridApi.isDestroyed?.()) {
              return;
            }
            if (this.dataProvider) {
              this.gridApi.refreshServerSide({ purge: true });
              return;
            }
            this.gridApi.refreshCells();
          }),
        )
        .subscribe() || new Subscription();
  }

  private isSortFilterApplied(): boolean {
    return (
      this.gridApi?.isAnyFilterPresent() ||
      this.gridApi?.getColumnState().some((cs) => {
        const defaulColDef = this.columnDefs.find((cd) => cd.field === cs.colId);
        return cs.sort && cs.sort !== defaulColDef?.initialSort;
      }) ||
      false
    );
  }

  setClearFilter(): void {
    this.disableClearFilter = !this.isSortFilterApplied();
  }

  modules: Module[] = [
    ServerSideRowModelModule,
    CellSelectionModule,
    SetFilterModule,
    ClipboardModule,
    ClientSideRowModelModule,
    AllCommunityModule,
    ExcelExportModule,
    TreeDataModule,
  ];

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    if (this.rowData$ || this.dataProvider) {
      this.gridApi?.setGridOption('loading', true);
    }
    this.gridApi?.setFilterModel(this.defaultFilter);

    if (this.dataProvider) {
      this.gridApi.setGridOption('serverSideDatasource', this.dataProvider());
    }

    this.gridReady.emit(event);
    if (this.rowData$) {
      this.rowData$.subscribe((data) => {
        if (!event.api.isDestroyed()) {
          event.api.updateGridOptions({
            rowData: data,
            loading: false,
          });
        }
      });
    }
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    this.gridApi = event.api;
    this.gridApi?.setGridOption('loading', false);
    this.firstDataRendered.emit(event);
  }

  // https://stackoverflow.com/questions/59053076/function-rowselected-has-been-called-twiceselected-and-deselected
  // https://www.ag-grid.com/javascript-data-grid/row-events/#reference-rowNodeEvents-rowSelected
  onRowSelected(event: RowSelectedEvent): void {
    if (!event.node.isSelected()) {
      return;
    }
    this.gridApi = event.api;
    this.rowSelected.emit(event);
  }

  onRowClicked(event: RowClickedEvent): void {
    this.gridApi = event.api;
    this.rowClicked.emit(event);
  }

  onSortChanged(event: SortChangedEvent): void {
    this.setClearFilter();
    this.sortChanged.emit(event);
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.setClearFilter();
    this.filterChanged.emit(event);
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    this.paginationChanged.emit(event);
  }

  onRowDataUpdated(event: RowDataUpdatedEvent): void {
    this.gridApi = event.api;
    this.rowDataUpdated.emit(event);
  }

  onModelUpdated(): void {
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.setGridOption('loading', false);
    }
  }
  onClearAllSortFilters(): void {
    this.gridApi?.setFilterModel({});
    this.gridApi.resetColumnState();
    this.gridApi?.paginationGoToFirstPage();
    this.clearAllSortFilters.emit();
  }

  processCellForClipboard(params: ProcessCellForExportParams): any {
    const columnName = params.column['colDef'].field;
    const customDatePipe = new CustomDatePipe();
    if (params?.node) {
      switch (columnName) {
        case 'serviceKind':
          return formatEntitlementCopyColumnContent(params.node.data.entitlementHeaders);
        case 'effectiveDate':
          return customDatePipe.transform(params.node.data.effectiveDate);
        case 'expiryDate':
          return customDatePipe.transform(params.node.data.expiryDate);
        case 'reservedOn':
          return customDatePipe.transform(params.node.data.reservedOn);
        case 'redeemBefore':
          return customDatePipe.transform(params.node.data.redeemBefore);
        case 'redeemedOn':
          return customDatePipe.transform(params.node.data.redeemedOn);
        case 'expiresOn':
          return customDatePipe.transform(params.node.data.expiresOn);
        case 'creditsActivity':
          return params.node.data.tokenConsumptionMapping(params.node);
        default:
          return params.value;
      }
    }
    return params.value;
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  constructor() {}
}
